import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { publicPostApi } from '../../api';
import Button from '../ui/Button';

const PasswordSchema = Yup.object().shape({
	password: Yup.string().required('New password is required'),
	confirmPassword: Yup.string()
		.required('Confirm password is required')
		.oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

const SetNewPassword = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const phone_number = location.state?.phone_number ?? '';
	const code = location.state?.code ?? '';

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (!phone_number || !code) {
			toast.error('Invalid or expired link. Please request a new code.');
			navigate('/forget-password', { replace: true });
		}
	}, [phone_number, code, navigate]);

	const handleSubmit = async (values) => {
		setIsLoading(true);
		publicPostApi('system/auth/password/reset/', {
				phone_number,
				code,
				password: values.password,
			})
			.then(() => {
				setIsLoading(false);
				toast.success('Password updated. You can log in.');
				navigate('/login', { replace: true });
			})
			.catch((error) => {
				setIsLoading(false);
				toast.error(error?.message ?? 'Something went wrong. Please try again.');
			});
	};

	if (!phone_number || !code) {
		return null;
	}

	return (
		<div className="overflow-hidden bg-white mx-auto mt-10 md:mt-20 mb-5 md:mb-10 p-4 md:p-6 w-full md:max-w-[600px] shadow-xl rounded-lg">
			<div className="text-center mb-6">
				<h2 className="text-3xl font-bold text-black">Set new password</h2>
				<p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">
					Enter your new password and confirm it.
				</p>
			</div>

			<Formik
				initialValues={{ password: '', confirmPassword: '' }}
				validationSchema={PasswordSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, touched }) => (
					<Form className="flex flex-col justify-center">
						<div className="grid grid-cols-1 gap-5">
							<div className="grid">
								<label
									htmlFor="password"
									className="block text-gray-500 font-medium text-sm leading-none mb-2"
								>
									New password
								</label>
								<Field
									id="password"
									name="password"
									type="password"
									placeholder="New password"
									className="py-2 px-4 w-full border text-sm rounded-md min-h-12 bg-white border-gray-200 focus:outline-none focus:border-brand-500"
									autoComplete="new-password"
								/>
								{errors.password && touched.password && (
									<span className="text-red-400 text-sm mt-2">{errors.password}</span>
								)}
							</div>
							<div className="grid">
								<label
									htmlFor="confirmPassword"
									className="block text-gray-500 font-medium text-sm leading-none mb-2"
								>
									Confirm password
								</label>
								<Field
									id="confirmPassword"
									name="confirmPassword"
									type="password"
									placeholder="Confirm password"
									className="py-2 px-4 w-full border text-sm rounded-md min-h-12 bg-white border-gray-200 focus:outline-none focus:border-brand-500"
									autoComplete="new-password"
								/>
								{errors.confirmPassword && touched.confirmPassword && (
									<span className="text-red-400 text-sm mt-2">{errors.confirmPassword}</span>
								)}
							</div>
							<Button type="submit" size="lg" fullWidth disabled={isLoading} className="my-1">
								{isLoading ? 'Updating…' : 'Update password'}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default SetNewPassword;
